/**
 * Une interaction (`cmi.interactions.n`) telle qu'envoyée à l'API (#158), au
 * format SCORM 2004. Seuls les champs déclarés par le module sont présents.
 */
export type ScormInteractionData = {
  index: number
  id: string
  type?: string
  description?: string
  correctResponses?: string[]
  learnerResponse?: string
  result?: string
  weighting?: number
  latency?: string
  timestamp?: string
  objectiveIds?: string[]
}

const INTERACTION_KEY = /^cmi\.interactions\.(\d+)\./

/** Éléments simples d'une interaction, nommés comme dans le modèle 2004. */
const SCALAR_FIELDS = {
  type: 'type',
  description: 'description',
  learnerResponse: 'learner_response',
  result: 'result',
  latency: 'latency',
  timestamp: 'timestamp',
} as const

/** Position `n` de l'interaction que vise un élément, ou `null` hors de `cmi.interactions`. */
export function interactionIndexOf(key: string): number | null {
  const match = INTERACTION_KEY.exec(key)
  return match ? Number.parseInt(match[1], 10) : null
}

/** Valeurs d'une sous-collection (`correct_responses.n.pattern`…), dans l'ordre de leurs positions. */
function collection(data: Record<string, string>, prefix: string, field: string): string[] | undefined {
  const values: string[] = []
  for (let position = 0; `${prefix}.${position}.${field}` in data; position++) {
    values.push(data[`${prefix}.${position}.${field}`])
  }
  return values.length > 0 ? values : undefined
}

/**
 * Lit l'interaction `index` dans le modèle de données du bridge. Sans
 * identifiant, l'interaction n'est pas encore exploitable : `null`.
 */
export function readInteraction(data: Record<string, string>, index: number): ScormInteractionData | null {
  const prefix = `cmi.interactions.${index}`
  const id = data[`${prefix}.id`]
  if (!id) return null

  const interaction: ScormInteractionData = { index, id }
  for (const [field, element] of Object.entries(SCALAR_FIELDS)) {
    const value = data[`${prefix}.${element}`]
    if (value) interaction[field as keyof typeof SCALAR_FIELDS] = value
  }
  const weighting = Number.parseFloat(data[`${prefix}.weighting`] ?? '')
  if (Number.isFinite(weighting)) interaction.weighting = weighting
  const correctResponses = collection(data, `${prefix}.correct_responses`, 'pattern')
  if (correctResponses) interaction.correctResponses = correctResponses
  const objectiveIds = collection(data, `${prefix}.objectives`, 'id')
  if (objectiveIds) interaction.objectiveIds = objectiveIds
  return interaction
}

/**
 * L'inverse de `readInteraction` : les éléments du modèle de données d'une
 * interaction déjà enregistrée, pour la rendre au module à la reprise (#158).
 */
export function interactionEntries(interaction: ScormInteractionData): Record<string, string> {
  const prefix = `cmi.interactions.${interaction.index}`
  const entries: Record<string, string> = { [`${prefix}.id`]: interaction.id }
  for (const [field, element] of Object.entries(SCALAR_FIELDS)) {
    const value = interaction[field as keyof typeof SCALAR_FIELDS]
    if (value) entries[`${prefix}.${element}`] = value
  }
  if (interaction.weighting != null) entries[`${prefix}.weighting`] = String(interaction.weighting)
  interaction.correctResponses?.forEach((pattern, position) => {
    entries[`${prefix}.correct_responses.${position}.pattern`] = pattern
  })
  interaction.objectiveIds?.forEach((id, position) => {
    entries[`${prefix}.objectives.${position}.id`] = id
  })
  return entries
}
